/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { format } from 'date-fns';
import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { ConsultantInvoiceCard } from '../../components/finance/consultant-invoice-card.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  useGenerateConsultantInvoice,
  useMyConsultantInvoices,
  useSubmitConsultantInvoice,
} from '../../lib/queries/consultant-invoices.js';

const INPUT =
  'focus-ring rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function DraftSubmit({ id }: { readonly id: string }): JSX.Element {
  const { t } = useTranslation();
  const submit = useSubmitConsultantInvoice();
  const onClick = useCallback(() => {
    submit.mutate({ id });
  }, [submit, id]);
  return (
    <Button type="button" onClick={onClick} disabled={submit.isPending}>
      {t('consultantInvoice.submit')}
    </Button>
  );
}

/** Therapist portal → Invoices (BRD v2.0 CR-006): a consultant generates + submits. */
export function ConsultantInvoicesPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyConsultantInvoices();
  const generate = useGenerateConsultantInvoice();
  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const onMonth = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMonth(e.target.value);
  }, []);
  const onGenerate = useCallback(() => {
    generate.mutate(month);
  }, [generate, month]);

  return (
    <div className="p-8">
      <PageHeader title={t('consultantInvoice.title')} subtitle={t('consultantInvoice.subtitle')} />

      <div className="mb-5 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
        <div>
          <label
            htmlFor="inv-month"
            className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300"
          >
            {t('consultantInvoice.month')}
          </label>
          <input id="inv-month" type="month" className={INPUT} value={month} onChange={onMonth} />
        </div>
        <Button type="button" onClick={onGenerate} disabled={generate.isPending}>
          {t('consultantInvoice.generate')}
        </Button>
        {generate.isError && (
          <p role="alert" className="text-sm text-red-600">
            {t('consultantInvoice.generateError')}
          </p>
        )}
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={(query.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('consultantInvoice.empty')}
      >
        <div className="space-y-3">
          {(query.data ?? []).map((invoice) => (
            <ConsultantInvoiceCard key={invoice.id} invoice={invoice}>
              {invoice.status === 'draft' && <DraftSubmit id={invoice.id} />}
            </ConsultantInvoiceCard>
          ))}
        </div>
      </QueryState>
    </div>
  );
}
